import * as z from 'zod/v4';

import {
  type Bet,
  type Board,
  BOARD_PROPS_QP,
  boardPropsEnum,
  type BoardType,
  type MOutcomes,
  type OutcomeDistribution,
  type Reduction,
  type SportkryssNumber,
  type UOutcomes,
} from '../coupon/entry.js';
import { type SystemType, systemTypeEnum } from '../system/entry.js';
import { parseErHexBoard } from './parsers/parseErHexBoard.js';
import { parseMSys1x2 } from './parsers/parseMSys1x2.js';
import { parseURow1x2 } from './parsers/parseURow1x2.js';

/** HEX Board data  */
type CompactBoardData = string[];

const itsSystemSchema = systemTypeEnum.or(z.literal('UROW'));

type BoardInfoType = {
  compactBoardData: CompactBoardData;
  systemType: SystemType;
  // TODO: should map the real systemNum type (branded type) instead of number?
  // this should also be cleaned up since we have both RSYS and USYS data here and nyckeltabeller in core-framework
  systemNumber: number;
};

const boardBaseSchema = z.object({
  boardProps: z.array(boardPropsEnum),
  systemType: z.array(itsSystemSchema),
  // TODO: should map the real systemNum type (branded type) instead of number?
  // this should also be cleaned up since we have both RSYS and USYS data here and nyckeltabeller in core-framework
  systemNum: z.number(),
});

const oneXTWoBetEventSchema = z.array(z.string());
export const oneXTwoBoardSchema = z
  .object({
    ...boardBaseSchema.shape,
    boardData: oneXTWoBetEventSchema,
  })
  .transform(({ boardData, systemType: systemTypes, systemNum, boardProps }) => {
    const mOutcomes: MOutcomes = [];
    const uOutcomes: UOutcomes = [];
    const outcomes: Bet = [];
    const compactBoardData: CompactBoardData = [];
    // TODO: Decide where to calculate outcome distribution.
    // For R and U systems this should be done in the core-framework
    // EXTERNA, SROW and MSYS can be done here, but will it create unnecessary inconsistency?
    const outcomeDistribution: OutcomeDistribution = [];

    let [systemType] = systemTypes;
    // Default to MSYS to remove 'undefined' from the TypeScript type
    systemType ??= 'MSYS';

    if (systemType === 'UROW') {
      const parsedUOutcomes = parseURow1x2(boardData);
      uOutcomes.push(...parsedUOutcomes);
    } else {
      const { mOutcomes: parsedMOutcomes, outcomes: parsedOutcomes } = parseMSys1x2(boardData);
      mOutcomes.push(...parsedMOutcomes);
      outcomes.push(...parsedOutcomes);
    }

    const quickPickSize = 0;
    const quickPickVariant = BOARD_PROPS_QP.find((prop) => boardProps.includes(prop));
    const type: BoardType = systemType === 'SROW' ? 'BOARD_SINGLE' : 'BOARD_MULTIPLE';
    const reduction: Reduction[] = [];

    const systemsWithSystemNumber: SystemType[] = ['RSYS', 'USYS'];
    const systemNumber = systemType === 'UROW' || systemsWithSystemNumber.includes(systemType) ? 0 : systemNum;

    const board: Board = {
      quickPickSize,
      quickPickVariant,
      type,
      reduction,
      mOutcomes,
      uOutcomes,
      outcomes,
      userOutcomes: [],
      outcomeDistribution,
    } as const;

    const boardInfo: BoardInfoType & { isURow: boolean } = {
      compactBoardData,
      systemNumber,
      // Temporary handle UROW as SROW, later UROW will be merged into the main board
      systemType: systemType === 'UROW' ? 'SROW' : systemType,
      isURow: systemType === 'UROW',
    } as const;

    return { board, boardInfo } as const;
  });

const binaryBoardDataSchema = z.array(z.string());
export const externalSystemsOneXTwoBoardSchema = z
  .object({
    simpleRowsOnly: z.boolean(),
    boards: binaryBoardDataSchema,
  })
  .transform(({ boards: boardsData, simpleRowsOnly: isSrow }) => {
    const boards: Board[] = [];
    const compactBoardData: CompactBoardData = [];

    for (const boardData of boardsData) {
      const mOutcomes: MOutcomes = [];
      const uOutcomes: UOutcomes = [];
      const outcomes: Bet = parseErHexBoard(boardData);
      // TODO: see todo in oneXTWoBetEventSchema
      const outcomeDistribution: OutcomeDistribution = [];

      const type: BoardType = isSrow ? 'BOARD_MULTIPLE' : 'BOARD_SINGLE';
      const reduction: Reduction[] = [];

      const board: Board = {
        quickPickSize: 0,
        quickPickVariant: undefined,
        type,
        reduction,
        mOutcomes,
        uOutcomes,
        outcomes,
        userOutcomes: [],
        outcomeDistribution,
      } as const;

      boards.push(board);
    }

    const systemType = isSrow ? 'SROW' : 'MSYS';
    const boardInfo: BoardInfoType = {
      compactBoardData,
      systemType,
      systemNumber: 0,
    } as const;

    return { boards, boardInfo } as const;
  });

const scoreBoardDataSchema = z.array(z.array(z.string())); // TODO: ["01"],["1"],["2"],["3"],["4"],["5"]
export const scoreBoardSchema = z.object({
  ...boardBaseSchema.shape,
  boardData: scoreBoardDataSchema,
});

export const sportkryssBoardSchema = z
  .object({
    ...boardBaseSchema.shape,
    systemType: boardBaseSchema.shape.systemType.nullish(),
    boardData: z.array(z.number()),
  })
  .transform(({ boardData }) => {
    return boardData.join('') as SportkryssNumber;
  });
